import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import remarkNestHeaderBullets from '@/lib/remarkNestHeaderBullets';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Loader2, BarChart3, MessageSquare, TrendingUp, DollarSign, AlertTriangle, Users, ChevronLeft, ChevronRight, ChevronDown, TestTube, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Lightweight inline citation renderer mirroring chat UI behavior
const AnswerWithCitations: any = ({ content, citations }: { content: string; citations?: Array<any> }) => {
  const orderedUniqueBracketRefs = useMemo(() => {
    try {
      const matches = Array.from(String(content || '').matchAll(/\[(\d+)\]/g)).map(m => parseInt(m[1], 10));
      const seen = new Set<number>();
      const ordered: number[] = [];
      for (const n of matches) {
        if (!Number.isNaN(n) && !seen.has(n)) {
          seen.add(n);
          ordered.push(n);
        }
      }
      return ordered;
    } catch {
      return [] as number[];
    }
  }, [content]);

  const refNumberToCitation = useMemo(() => {
    const map = new Map<number, any>();
    if (Array.isArray(citations) && citations.length > 0) {
      orderedUniqueBracketRefs.forEach((refNum, idx) => {
        const citation = citations[idx];
        if (citation) map.set(refNum, citation);
      });
    }
    return map;
  }, [citations, orderedUniqueBracketRefs]);

  // Map original reference numbers -> display numbers (1..N by first mention)
  const displayNumberMap = useMemo(() => {
    const map = new Map<number, number>();
    orderedUniqueBracketRefs.forEach((refNum, idx) => map.set(refNum, idx + 1));
    return map;
  }, [orderedUniqueBracketRefs]);

  const InlineCitation = ({ refNum }: { refNum: number }) => {
    const citation = refNumberToCitation.get(refNum);
    const tip = (
      <div>
        <div className="text-xs font-semibold mb-1">{citation?.title || 'Referenced context'}</div>
        {citation?.summary ? (
          <p className="text-xs text-muted-foreground leading-relaxed">{citation.summary}</p>
        ) : citation?.snippet ? (
          <p className="text-xs text-muted-foreground leading-relaxed">{citation.snippet}</p>
        ) : (
          <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
        )}
      </div>
    );

    return (
      <span className="align-super ml-0.5 inline-flex">
        <span className="hidden md:inline">
          <HoverCard>
            <HoverCardTrigger asChild>
              <sup className="cursor-pointer text-[0.65em]" aria-label={`View citation ${refNum}`}>
                <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] leading-none">{refNum}</span>
              </sup>
            </HoverCardTrigger>
            <HoverCardContent side="top" align="center" className="w-80">{tip}</HoverCardContent>
          </HoverCard>
        </span>
        <span className="inline md:hidden">
          <Popover>
            <PopoverTrigger asChild>
              <sup className="cursor-pointer text-[0.65em]" aria-label={`View citation ${refNum}`}>
                <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] leading-none">{refNum}</span>
              </sup>
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-80">{tip}</PopoverContent>
          </Popover>
        </span>
      </span>
    );
  };

  const transformInline = (input: string): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    const re = /\[(\d+)\]/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(input)) !== null) {
      const start = match.index;
      const before = input.slice(lastIndex, start);
      if (before) out.push(before);
      const refNum = parseInt(match[1], 10);
      if (!Number.isNaN(refNum)) out.push(
        <span key={`c-${start}`} className="align-super ml-0.5 inline-flex">
          <span className="hidden md:inline">
            <HoverCard>
              <HoverCardTrigger asChild>
                <sup className="cursor-pointer text-[0.65em]" aria-label={`View citation ${displayNumberMap.get(refNum) || refNum}`}>
                  <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] leading-none">{displayNumberMap.get(refNum) || refNum}</span>
                </sup>
              </HoverCardTrigger>
              <HoverCardContent side="top" align="center" className="w-80">
                <div>
                  <div className="text-xs font-semibold mb-1">{refNumberToCitation.get(refNum)?.title || 'Referenced context'}</div>
                  {refNumberToCitation.get(refNum)?.summary ? (
                    <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.summary}</p>
                  ) : refNumberToCitation.get(refNum)?.snippet ? (
                    <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.snippet}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
                  )}
                </div>
              </HoverCardContent>
            </HoverCard>
          </span>
          <span className="inline md:hidden">
            <Popover>
              <PopoverTrigger asChild>
                <sup className="cursor-pointer text-[0.65em]" aria-label={`View citation ${displayNumberMap.get(refNum) || refNum}`}>
                  <span className="inline-flex items-center justify-center rounded-full bg-primary text-white font-semibold px-1.5 py-[1px] leading-none">{displayNumberMap.get(refNum) || refNum}</span>
                </sup>
              </PopoverTrigger>
              <PopoverContent side="top" align="center" className="w-80">
                <div>
                  <div className="text-xs font-semibold mb-1">{refNumberToCitation.get(refNum)?.title || 'Referenced context'}</div>
                  {refNumberToCitation.get(refNum)?.summary ? (
                    <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.summary}</p>
                  ) : refNumberToCitation.get(refNum)?.snippet ? (
                    <p className="text-xs text-muted-foreground leading-relaxed">{refNumberToCitation.get(refNum)?.snippet}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground leading-relaxed">Source details not available for this reference.</p>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </span>
        </span>
      );
      lastIndex = start + match[0].length;
    }
    const rest = input.slice(lastIndex);
    if (rest) out.push(rest);
    return out;
  };

  const renderWithCitations = (children: React.ReactNode) => {
    return React.Children.map(children as any, (child) => {
      if (typeof child === 'string') return <>{transformInline(child)}</>;
      return child;
    });
  };

  return (
    <ReactMarkdown
      className="prose dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
      remarkPlugins={[remarkGfm, remarkBreaks, remarkNestHeaderBullets]}
      components={{
        p: ({ children }) => (
          <p className="mb-2 last:mb-0">{renderWithCitations(children)}</p>
        ),
        ul: ({ children }) => <ul className="list-disc pl-6 space-y-1 mb-2 last:mb-0">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-6 space-y-1 mb-2 last:mb-0">{children}</ol>,
        li: ({ children }) => (
          <li>{renderWithCitations(children)}</li>
        ),
        h1: ({ children }) => (
          <h1 className="mt-4 mb-2 text-2xl md:text-3xl font-semibold">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="mt-4 mb-2 text-xl md:text-2xl font-semibold">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="mt-2 mb-2 text-base font-semibold">{children}</h3>
        ),
        blockquote: ({ children }) => (
          <blockquote className="my-3 border-l-2 border-muted pl-3 italic text-muted-foreground">{children}</blockquote>
        ),
        hr: () => <hr className="my-3 border-border/60" />,
        table: ({ children }) => (
          <div className="my-3 overflow-x-auto">
            <table className="w-full text-sm border border-border/60 rounded-md">{children}</table>
          </div>
        ),
        thead: ({ children }) => <thead className="bg-muted/60">{children}</thead>,
        tbody: ({ children }) => <tbody>{children}</tbody>,
        tr: ({ children }) => <tr className="border-b border-border/50">{children}</tr>,
        th: ({ children }) => (
          <th className="text-left px-3 py-2 font-medium">{children}</th>
        ),
        td: ({ children }) => <td className="px-3 py-2 align-top">{children}</td>,
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/90 underline transition-colors">
            {children}
          </a>
        ),
        code: ({ children }) => (
          <code className="bg-muted px-2 py-1 rounded text-sm font-mono border">{children}</code>
        ),
        pre: ({ children }) => (
          <pre className="bg-muted p-4 rounded-[var(--radius)] overflow-x-auto text-sm border">{children}</pre>
        )
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

// Placeholder for potential future global portal mounts
AnswerWithCitations.Definitions = () => null;

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('questions');

  // Fetch real analytics data
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['analytics-dashboard', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get user's posits
      const { data: posits } = await supabase
        .from('posits')
        .select('id, is_monetized, subscription_price')
        .eq('user_id', user.id);

      const positIds = posits?.map(p => p.id) || [];

      // Calculate date ranges
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Total questions across all posits (user role)
      const { count: totalQuestions } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds)
        .eq('role', 'user')
        .eq('source', 'posit_query');

      // Proper month windows
      const { count: lastMonthQuestions } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds)
        .eq('role', 'user')
        .eq('source', 'posit_query')
        .gte('created_at', lastMonthStart.toISOString())
        .lt('created_at', thisMonthStart.toISOString());

      const { count: thisMonthQuestions } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds)
        .eq('role', 'user')
        .eq('source', 'posit_query')
        .gte('created_at', thisMonthStart.toISOString());

      // Active posits (posits that received questions in last 30 days)
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const { data: activePositsData } = await supabase
        .from('chat_messages')
        .select('posit_id')
        .in('posit_id', positIds)
        .eq('role', 'user')
        .eq('source', 'posit_query')
        .gte('created_at', thirtyDaysAgo.toISOString());

      const activePosits = new Set(activePositsData?.map(m => m.posit_id) || []).size;

      // Average response time using timing_metadata from assistant messages (last week)
      const { data: recentAssistant } = await supabase
        .from('chat_messages')
        .select('timing_metadata')
        .in('posit_id', positIds)
        .eq('role', 'assistant')
        .gte('created_at', lastWeek.toISOString())
        .order('created_at', { ascending: false })
        .limit(2000);

      const latencies = (recentAssistant || [])
        .map((m: any) => Number(m?.timing_metadata?.total_latency_ms || 0))
        .filter((n) => Number.isFinite(n) && n > 0);
      const avgResponseTime = latencies.length > 0 ? (latencies.reduce((a, b) => a + b, 0) / latencies.length) / 1000 : 0;

      // Calculate MRR from active subscriptions per posit
      const monetizedPosits = posits?.filter(p => p.is_monetized) || [];
      const monetizedIds = monetizedPosits.map(p => p.id);
      let totalRevenue = 0;
      if (monetizedIds.length > 0) {
        const { data: activeSubs } = await supabase
          .from('posit_subscriptions')
          .select('posit_id, status')
          .in('posit_id', monetizedIds)
          .eq('status', 'active');

        const countByPosit: Record<string, number> = {};
        (activeSubs || []).forEach((row: any) => {
          countByPosit[row.posit_id] = (countByPosit[row.posit_id] || 0) + 1;
        });

        totalRevenue = monetizedPosits.reduce((sum, p) => {
          const subCount = countByPosit[p.id] || 0;
          const price = p.subscription_price || 0;
          return sum + subCount * price;
        }, 0);
      }

      // Month-over-month growth
      const monthlyGrowth = (lastMonthQuestions || 0) > 0
        ? (((thisMonthQuestions || 0) - (lastMonthQuestions || 0)) / (lastMonthQuestions || 1)) * 100
        : 0;

      return {
        totalQuestions: totalQuestions || 0,
        totalPosits: activePosits,
        avgResponseTime: Math.round(avgResponseTime * 10) / 10,
        totalRevenue: Math.round(totalRevenue),
        monthlyGrowth: Math.round(monthlyGrowth * 10) / 10,
        activeUsers: 0, // TODO: Implement unique user tracking when user system is expanded
        hasMonetized: (monetizedPosits?.length || 0) > 0,
      };
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const stats = analyticsData || {
    totalQuestions: 0,
    totalPosits: 0,
    avgResponseTime: 0,
    totalRevenue: 0,
    monthlyGrowth: 0,
    activeUsers: 0,
    hasMonetized: false,
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">Please sign in to view analytics</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Analytics"
      description="Comprehensive insights into your posit performance and audience engagement"
    >
      <AnswerWithCitations.Definitions />
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Questions</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.totalQuestions.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.monthlyGrowth >= 0 ? '+' : ''}{stats.monthlyGrowth}% from last month
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Posits</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : stats.totalPosits}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.totalPosits > 0 ? 'Receiving questions' : 'No recent activity'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : `${stats.avgResponseTime}s`}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.avgResponseTime < 5 ? 'Excellent speed' : 'Good performance'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoading ? <Loader2 className="h-6 w-6 animate-spin" /> : `$${stats.totalRevenue}`}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.totalRevenue > 0 ? 'Monthly recurring' : 'Enable monetization'}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-6 h-auto gap-1">
          <TabsTrigger value="questions" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm p-2 md:p-3">
            <MessageSquare className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden sm:inline">Question Stream</span>
            <span className="sm:hidden">Questions</span>
          </TabsTrigger>
          <TabsTrigger value="performance" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm p-2 md:p-3">
            <BarChart3 className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden sm:inline">Posit Performance</span>
            <span className="sm:hidden">Performance</span>
          </TabsTrigger>
          {/* Replaced internal-only Quality Loop with Conversations tab for creators */}
          <TabsTrigger value="conversations" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm p-2 md:p-3">
            <MessageSquare className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden sm:inline">Conversations</span>
            <span className="sm:hidden">Convos</span>
          </TabsTrigger>
          <TabsTrigger value="gaps" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm p-2 md:p-3">
            <AlertTriangle className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden sm:inline">Knowledge Gaps</span>
            <span className="sm:hidden">Gaps</span>
          </TabsTrigger>
          <TabsTrigger value="revenue" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm p-2 md:p-3">
            <DollarSign className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden sm:inline">Revenue Insights</span>
            <span className="sm:hidden">Revenue</span>
          </TabsTrigger>
          <TabsTrigger value="training" className="flex items-center gap-1 md:gap-2 text-xs md:text-sm p-2 md:p-3">
            <TestTube className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden sm:inline">Training Queue</span>
            <span className="sm:hidden">Training</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="questions" className="mt-6">
          <QuestionStreamTab />
        </TabsContent>

        <TabsContent value="performance" className="mt-6">
          <PositPerformanceTab />
        </TabsContent>

        <TabsContent value="conversations" className="mt-6">
          <ConversationsTab />
        </TabsContent>

        <TabsContent value="gaps" className="mt-6">
          <KnowledgeGapsTab />
        </TabsContent>

        {stats.hasMonetized && (
          <TabsContent value="revenue" className="mt-6">
            <RevenueInsightsTab />
          </TabsContent>
        )}

        <TabsContent value="training" className="mt-6">
          <TrainingQueueTab />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};
// Quality Loop Tab – surface core KPIs using placeholder data until aggregates are wired
const QualityLoopTab = () => {
  const { user } = useAuth();
  const { data: qualityData, isLoading } = useQuery({
    queryKey: ['quality-loop-kpis', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      // Placeholder: when ready, fetch from a view like `posit_kpi_daily_latest`
      // Return shape compatible with UI tiles
      return {
        citationRate: 82.5,
        gapCreationRate: 3.2,
        resolutionRate7d: 46.0,
        timeToResolutionHrs: 36.4,
        trainingThroughputWeekly: 14,
        trendWeeks: Array.from({ length: 8 }).map((_, i) => ({ week: i + 1, citationRate: 75 + i, gapsPer100: 5 - i * 0.2 })),
        recentOpenGaps: [],
      };
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,
  });

  const data = qualityData || {
    citationRate: 0,
    gapCreationRate: 0,
    resolutionRate7d: 0,
    timeToResolutionHrs: 0,
    trainingThroughputWeekly: 0,
    trendWeeks: [],
    recentOpenGaps: [],
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quality Loop</CardTitle>
        <CardDescription>Close-the-loop metrics from gaps to training and embedding</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading quality metrics...</span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* KPI Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Citation Rate</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{data.citationRate.toFixed(1)}%</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Gap Creation Rate</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{data.gapCreationRate.toFixed(1)}/100</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Resolution Rate (7d)</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{data.resolutionRate7d.toFixed(1)}%</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Time to Resolution</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{data.timeToResolutionHrs.toFixed(1)}h</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Training Throughput</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{data.trainingThroughputWeekly}/wk</div></CardContent>
              </Card>
            </div>

            {/* Trend Placeholder */}
            <div className="p-4 border rounded-lg text-sm text-muted-foreground">
              <div className="font-medium mb-2">Trend (last 8–12 weeks)</div>
              <div>Placeholder chart: Citation rate vs. Gaps per 100 answers by week</div>
            </div>

            {/* Recent Open Gaps Table Placeholder */}
            <div className="p-4 border rounded-lg">
              <div className="font-medium mb-2">Recent Open Gaps</div>
              <div className="text-sm text-muted-foreground">Table will list open gaps with quick links to Tasks</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Question Stream Tab Component with Pagination
const QuestionStreamTab = () => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    return { from: start, to: end };
  });
  const [positFilter, setPositFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedAnswerById, setExpandedAnswerById] = useState<Record<string, boolean>>({});
  const questionsPerPage = 10;

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  const { data: questionsData, isLoading } = useQuery({
    queryKey: ['question-stream', user?.id, dateRange?.from?.toISOString() || 'nofrom', dateRange?.to?.toISOString() || 'noto', positFilter, currentPage, debouncedSearch],
    queryFn: async () => {
      if (!user?.id) return { questions: [], totalCount: 0 };

      // Get user's posits first
      const { data: posits } = await supabase
        .from('posits')
        .select('id, title')
        .eq('user_id', user.id);

      const positIds = posits?.map(p => p.id) || [];
      if (positIds.length === 0) return { questions: [], totalCount: 0 };

      // Calculate date range from picker
      let dateFrom: Date;
      let dateTo: Date | null = null;
      if (dateRange?.from) {
        dateFrom = new Date(dateRange.from);
        dateFrom.setHours(0, 0, 0, 0);
      } else {
        dateFrom = new Date();
        dateFrom.setFullYear(dateFrom.getFullYear() - 1);
      }
      if (dateRange?.to) {
        dateTo = new Date(dateRange.to);
        dateTo.setHours(23, 59, 59, 999);
      }

      // Get total count for pagination
      let countQuery = supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds)
        .eq('role', 'user')
        .eq('source', 'posit_query')
        .gte('created_at', dateFrom.toISOString());
      if (dateTo) {
        countQuery = countQuery.lte('created_at', dateTo.toISOString());
      }

      if (positFilter !== 'all') {
        countQuery = countQuery.eq('posit_id', positFilter);
      }

      if (debouncedSearch) {
        countQuery = countQuery.ilike('content', `%${debouncedSearch}%`);
      }

      const { count: totalCount } = await countQuery;

      // Get paginated questions
      let query = supabase
        .from('chat_messages')
        .select(`
          id,
          content,
          created_at,
          posit_id,
          chat_history_id,
          posits!inner (
            id,
            title
          )
        `)
        .in('posit_id', positIds)
        .eq('role', 'user')
        .eq('source', 'posit_query')
        .gte('created_at', dateFrom.toISOString())
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * questionsPerPage, currentPage * questionsPerPage - 1);

      if (dateTo) {
        query = query.lte('created_at', dateTo.toISOString());
      }

      if (positFilter !== 'all') {
        query = query.eq('posit_id', positFilter);
      }

      if (debouncedSearch) {
        query = query.ilike('content', `%${debouncedSearch}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      const questionsWithAnswers = await Promise.all(
        (data || []).map(async (q: any) => {
          // Try to find the first assistant response after this question
          const fiveMinutesAfter = new Date(new Date(q.created_at).getTime() + 5 * 60 * 1000).toISOString();
          let assistantQuery = supabase
            .from('chat_messages')
            .select('id, content, created_at, citations')
            .eq('posit_id', q.posit_id)
            .eq('role', 'assistant')
            .gte('created_at', q.created_at)
            .lte('created_at', fiveMinutesAfter)
            .order('created_at', { ascending: true })
            .limit(1);

          if (q.chat_history_id) {
            assistantQuery = assistantQuery.eq('chat_history_id', q.chat_history_id);
          }

          const { data: assistant, error: aerr } = await assistantQuery;
          if (aerr) {
            return { ...q, assistant: null };
          }
          return { ...q, assistant: assistant?.[0] || null };
        })
      );

      return {
        questions: questionsWithAnswers,
        totalCount: totalCount || 0
      };
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds for "real-time" feel
  });

  const { data: userPosits } = useQuery({
    queryKey: ['user-posits-filter', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('posits')
        .select('id, title')
        .eq('user_id', user.id)
        .order('title');
      return data || [];
    },
    enabled: !!user?.id,
  });

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const questions = questionsData?.questions || [];
  const totalCount = questionsData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / questionsPerPage);

  const [feedbackByMessageId, setFeedbackByMessageId] = useState<Record<string, 'up' | 'down'>>({});

  const handleFeedback = async (assistantMessageId: string, positId: string, rating: 'up' | 'down', assistantContent?: string) => {
    try {
      setFeedbackByMessageId((prev) => ({ ...prev, [assistantMessageId]: rating }));
      await supabase.from('message_events').insert({
        posit_id: positId,
        user_id: user?.id || null,
        chat_message_id: assistantMessageId,
        sender_type: 'ai',
        event_type: 'chat',
        status: 'success',
        user_rating: rating === 'up' ? 5 : 1,
        message_content: assistantContent || null,
      });
    } catch (e) {
      // Best-effort; silently ignore
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [positFilter, debouncedSearch, dateRange?.from, dateRange?.to]);

  const toggleAnswer = (id: string) => {
    setExpandedAnswerById(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatRangeLabel = (range?: DateRange) => {
    try {
      const from = range?.from ? new Date(range.from) : null;
      const to = range?.to ? new Date(range.to) : null;
      const fmt = (d: Date) => d.toLocaleDateString();
      if (from && to) return `${fmt(from)} - ${fmt(to)}`;
      if (from) return fmt(from);
      return 'Dates';
    } catch {
      return 'Dates';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-lg md:text-xl">Real-Time Question Stream</CardTitle>
            <CardDescription className="text-sm">Live feed of questions being asked across your posits</CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={positFilter}
              onChange={(e) => setPositFilter(e.target.value)}
              className="px-2 py-1 text-xs md:text-sm border border-border rounded-md bg-background min-w-0"
            >
              <option value="all">All Posits</option>
              {userPosits?.map(posit => (
                <option key={posit.id} value={posit.id}>
                  {posit.title.length > 20 ? `${posit.title.substring(0, 20)}...` : posit.title}
                </option>
              ))}
            </select>
            {/* Desktop date picker */}
            <div className="hidden sm:block min-w-[240px]">
              <DatePickerWithRange date={dateRange as DateRange} setDate={setDateRange} />
            </div>
            {/* Mobile date filter button with popover */}
                <div className="sm:hidden">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="justify-between w-full">
                    {formatRangeLabel(dateRange)}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-2 w-[300px]" align="start">
                  <DatePickerWithRange date={dateRange as DateRange} setDate={setDateRange} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="w-full md:w-[260px]">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search questions…"
                className="text-xs md:text-sm"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading questions...</span>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No questions found for the selected period</p>
            <p className="text-sm mt-2">Questions will appear here as users interact with your Posits</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {questions.map((question: any) => (
                <div
                  key={question.id}
                  className="flex items-start gap-2 md:gap-3 p-3 md:p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-2">
                      <Badge variant="outline" className="text-xs w-fit">
                        {question.posits.title}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatTimeAgo(question.created_at)}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed break-words">{question.content}</p>

                    {question.assistant && (
                      <div className="mt-3 pl-3 border-l">
                        <button
                          className="w-full flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
                          onClick={() => toggleAnswer(question.assistant.id)}
                        >
                          <span>Answer</span>
                          <ChevronDown className={`h-4 w-4 transition-transform ${expandedAnswerById[question.assistant.id] ? 'rotate-180' : ''}`} />
                        </button>
                        {expandedAnswerById[question.assistant.id] && (
                          <div className="mt-2">
                            <div className="text-sm text-muted-foreground">
                              <AnswerWithCitations content={question.assistant.content} citations={(question.assistant as any).citations || []} />
                            </div>
                            <div className="mt-2 flex items-center justify-end gap-1">
                              <button
                                className={`p-1 rounded hover:bg-muted transition-colors ${feedbackByMessageId[question.assistant.id] === 'up' ? 'text-green-600' : 'text-muted-foreground'}`}
                                title="Thumbs up"
                                onClick={() => handleFeedback(question.assistant.id, question.posit_id, 'up', question.assistant.content)}
                              >
                                <ThumbsUp className="h-4 w-4" />
                              </button>
                              <button
                                className={`p-1 rounded hover:bg-muted transition-colors ${feedbackByMessageId[question.assistant.id] === 'down' ? 'text-red-600' : 'text-muted-foreground'}`}
                                title="Thumbs down"
                                onClick={() => handleFeedback(question.assistant.id, question.posit_id, 'down', question.assistant.content)}
                              >
                                <ThumbsDown className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * questionsPerPage) + 1} to {Math.min(currentPage * questionsPerPage, totalCount)} of {totalCount} questions
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
        
        {questions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              Auto-refreshes every 30 seconds
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PositPerformanceTab = () => {
  const { user } = useAuth();

  const { data: positPerformance, isLoading } = useQuery({
    queryKey: ['posit-performance', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Get user's posits with basic info
      const { data: posits } = await supabase
        .from('posits')
        .select('id, title, privacy, is_monetized, subscription_price, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (!posits || posits.length === 0) return [];
      const positIds = posits.map(p => p.id);

      // Batch fetch: questions all-time by posit
      const { data: allQuestions } = await supabase
        .from('chat_messages')
        .select('posit_id')
        .in('posit_id', positIds)
        .eq('role', 'user')
        .eq('source', 'posit_query');

      // Batch fetch: questions last 30 days by posit
      const since30 = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data: recentQuestions } = await supabase
        .from('chat_messages')
        .select('posit_id')
        .in('posit_id', positIds)
        .eq('role', 'user')
        .eq('source', 'posit_query')
        .gte('created_at', since30);

      // Batch fetch: assistant responses last 30 days for gap detection
      const { data: assistantResponses } = await supabase
        .from('chat_messages')
        .select('posit_id, content')
        .in('posit_id', positIds)
        .eq('role', 'assistant')
        .gte('created_at', since30);

      // Aggregate
      const totalByPosit = new Map<string, number>();
      (allQuestions || []).forEach((m: any) => totalByPosit.set(m.posit_id, (totalByPosit.get(m.posit_id) || 0) + 1));

      const recentByPosit = new Map<string, number>();
      (recentQuestions || []).forEach((m: any) => recentByPosit.set(m.posit_id, (recentByPosit.get(m.posit_id) || 0) + 1));

      const gapsByPosit = new Map<string, number>();
      (assistantResponses || []).forEach((r: any) => {
        const content = String(r.content || '').toLowerCase();
        const isGap = (
          (content.includes('sorry') && (content.includes('unable') || content.includes('cannot')))
          || (content.includes("don't have") && content.includes('information'))
          || (content.includes('cannot provide') && content.includes('answer'))
          || (content.includes('insufficient') && content.includes('information'))
          || (content.includes('need more') && content.includes('context'))
          || (content.includes('unclear') && content.includes('question'))
        );
        if (isGap) gapsByPosit.set(r.posit_id, (gapsByPosit.get(r.posit_id) || 0) + 1);
      });

      // Compose final array
      return posits.map((posit: any) => ({
        ...posit,
        totalQuestions: totalByPosit.get(posit.id) || 0,
        recentQuestions: recentByPosit.get(posit.id) || 0,
        knowledgeGaps: gapsByPosit.get(posit.id) || 0,
        subscribers: 0,
        monthlyRevenue: posit.is_monetized ? (posit.subscription_price || 0) * 0 : 0,
      }));
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Posit Performance Metrics</CardTitle>
          <CardDescription>Detailed performance analytics for each of your Posits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading performance data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!positPerformance || positPerformance.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Posit Performance Metrics</CardTitle>
          <CardDescription>Detailed performance analytics for each of your Posits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No posits found</p>
            <p className="text-sm mt-2">Create your first Posit to see performance metrics here</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Posit Performance Metrics</CardTitle>
        <CardDescription>Detailed performance analytics for each of your Posits</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {positPerformance.map((posit) => (
            <div
              key={posit.id}
              className="flex flex-col lg:flex-row lg:items-center lg:justify-between p-3 md:p-4 border border-border rounded-lg hover:shadow-md transition-all gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <h3 className="font-medium text-sm md:text-base truncate">{posit.title}</h3>
                  <Badge 
                    variant={posit.privacy === 'public' ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {posit.privacy}
                  </Badge>
                  {posit.is_monetized && (
                    <Badge variant="outline" className="text-xs">
                      ${posit.subscription_price}/mo
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-xs md:text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Questions</p>
                    <p className="font-medium">{posit.totalQuestions}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last 30 Days</p>
                    <p className="font-medium text-blue-600">{posit.recentQuestions}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Knowledge Gaps</p>
                    <p className="font-medium text-orange-600">
                      {posit.knowledgeGaps}
                      {posit.knowledgeGaps > 0 && (
                        <AlertTriangle className="w-3 h-3 inline ml-1" />
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Revenue</p>
                    <p className="font-medium text-green-600">
                      {posit.is_monetized ? `$${posit.monthlyRevenue.toFixed(0)}/mo` : 'Free'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-row lg:flex-col gap-2 lg:flex-shrink-0">
                <Button variant="outline" size="sm" className="text-xs" asChild>
                  <Link to={`/chat/${posit.id}`}>
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Chat
                  </Link>
                </Button>
                <Button variant="outline" size="sm" className="text-xs" asChild>
                  <Link to={`/update-posit/${posit.id}`}>
                    Edit
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Showing {positPerformance.length} posits</span>
            <span>Data refreshed {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const KnowledgeGapsTab = () => {
  const { user } = useAuth();
  const [selectedPosit, setSelectedPosit] = useState('all');
  const [dismissedGaps, setDismissedGaps] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const gapsPerPage = 5;
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const { data: knowledgeGapsData, isLoading } = useQuery({
    queryKey: ['knowledge-gaps', user?.id, selectedPosit, currentPage],
    queryFn: async () => {
      if (!user?.id) return { gaps: [], totalCount: 0 };

      // Get user's posits first
      const { data: posits } = await supabase
        .from('posits')
        .select('id, title')
        .eq('user_id', user.id);

      const positIds = posits?.map(p => p.id) || [];
      if (positIds.length === 0) return { gaps: [], totalCount: 0 };

      // Look for AI responses that indicate inability to answer
      let query = supabase
        .from('chat_messages')
        .select(`
          id,
          content,
          created_at,
          posit_id,
          posits!inner (
            id,
            title
          )
        `)
        .in('posit_id', positIds)
        .eq('role', 'assistant')
        .order('created_at', { ascending: false });

      if (selectedPosit !== 'all') {
        query = query.eq('posit_id', selectedPosit);
      }

      const { data: responses, error } = await query;
      if (error) throw error;

      // Filter responses that contain inability patterns
      const gapResponses = responses?.filter(response => {
        const content = response.content.toLowerCase();
        return (
          content.includes('sorry') && (content.includes('unable') || content.includes('cannot')) ||
          content.includes("don't have") && content.includes('information') ||
          content.includes('cannot provide') && content.includes('answer') ||
          content.includes('insufficient') && content.includes('information') ||
          content.includes('need more') && content.includes('context') ||
          content.includes('unclear') && content.includes('question') ||
          content.includes('sorry') && content.includes("don't have") ||
          content.includes('cannot') && content.includes('access') ||
          content.includes('unable') && content.includes('answer')
        );
      }) || [];

      // For each gap response, get the preceding user question (limit to one aggregated query is complex; keep scoped for now)
      const knowledgeGapsWithQuestions = await Promise.all(
        gapResponses.map(async (response) => {
          const fiveMinutesBefore = new Date(new Date(response.created_at).getTime() - 5 * 60 * 1000);
          const { data: userMessages } = await supabase
            .from('chat_messages')
            .select('id, content, created_at')
            .eq('posit_id', response.posit_id)
            .eq('role', 'user')
            .eq('source', 'posit_query')
            .lt('created_at', response.created_at)
            .gte('created_at', fiveMinutesBefore.toISOString())
            .order('created_at', { ascending: false })
            .limit(5);
          const userMessage = userMessages?.[0];
          return {
            id: response.id,
            question: userMessage?.content || 'Question not found',
            response: response.content || 'Response not available',
            posit: response.posits,
            created_at: response.created_at,
            gap_type: determineGapType(response.content),
            questionTime: userMessage?.created_at || response.created_at
          };
        })
      );

      // Apply pagination to the gaps
      const totalCount = knowledgeGapsWithQuestions.length;
      const startIndex = (currentPage - 1) * gapsPerPage;
      const endIndex = startIndex + gapsPerPage;
      const paginatedGaps = knowledgeGapsWithQuestions.slice(startIndex, endIndex);

      return {
        gaps: paginatedGaps,
        totalCount
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: userPosits } = useQuery({
    queryKey: ['user-posits-gaps', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('posits')
        .select('id, title')
        .eq('user_id', user.id)
        .order('title');
      return data || [];
    },
    enabled: !!user?.id,
  });

     const determineGapType = (responseContent: string) => {
     const content = responseContent.toLowerCase();
     if (content.includes('unclear') || content.includes('need more context')) return 'unclear';
     if (content.includes("don't have") || content.includes('insufficient') || content.includes('cannot provide the full content')) return 'missing-info';
     if (content.includes('unable') || content.includes('cannot access') || content.includes('sorry') && content.includes('cannot')) return 'unable';
     return 'missing-info'; // Default to missing-info for most cases
   };

  const getGapTypeColor = (type: string) => {
    switch (type) {
      case 'unclear': return 'bg-yellow-100 text-yellow-800';
      case 'missing-info': return 'bg-red-100 text-red-800';
      case 'unable': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return `${Math.floor(diffInDays / 30)} months ago`;
  };

  const handleDismissGap = (gapId: string) => {
    setDismissedGaps(prev => new Set([...prev, gapId]));
  };

  const handleAnswerChange = (gapId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [gapId]: value }));
  };

  const handleSubmitAnswer = async (gap: any) => {
    const content = (answers[gap.id] || '').trim();
    if (!content) {
      toast({ title: 'Answer required', description: 'Please write an answer before saving.', variant: 'destructive' });
      return;
    }
    try {
      setSavingId(gap.id);
      const payload = {
        posit_id: gap.posit.id,
        user_id: user?.id,
        fragments: [
          {
            id: `gap_${gap.id}`,
            content: `Q: ${gap.question}\nA: ${content}`,
            metadata: {
              type: 'gap_answer',
              title: 'Inline gap answer',
              cognitive_category: 'facts',
              depth_level: 1,
              confidence_score: 0.9,
            },
          },
        ],
      } as any;

      const { error } = await supabase.functions.invoke('update-vectors-simple', {
        body: payload,
      });
      if (error) throw error;

      toast({ title: 'Saved', description: 'Answer added to your knowledge base.' });
      handleDismissGap(gap.id);
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message || 'Please try again.', variant: 'destructive' });
    } finally {
      setSavingId(null);
    }
  };

  // Extract data from the paginated response
  const knowledgeGaps = knowledgeGapsData?.gaps || [];
  const totalCount = knowledgeGapsData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / gapsPerPage);

  // Filter out dismissed gaps
  const visibleGaps = knowledgeGaps.filter(gap => !dismissedGaps.has(gap.id));

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPosit]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Knowledge Gaps</CardTitle>
          <CardDescription>Questions that couldn't be answered - opportunities to improve</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Analyzing knowledge gaps...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
             <CardHeader>
         <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
           <div>
             <CardTitle className="text-lg md:text-xl">Knowledge Gaps</CardTitle>
             <CardDescription className="text-sm">Questions that couldn't be answered - opportunities to improve</CardDescription>
           </div>
           <select
             value={selectedPosit}
             onChange={(e) => setSelectedPosit(e.target.value)}
             className="px-2 py-1 text-xs md:text-sm border border-border rounded-md bg-background w-full md:w-auto"
           >
             <option value="all">All Posits</option>
             {userPosits?.map(posit => (
               <option key={posit.id} value={posit.id}>
                 {posit.title.length > 20 ? `${posit.title.substring(0, 20)}...` : posit.title}
               </option>
             ))}
           </select>
         </div>
       </CardHeader>
             <CardContent>
         {visibleGaps.length === 0 ? (
           <div className="text-center py-8 text-muted-foreground">
             <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
             <p>No knowledge gaps detected</p>
             <p className="text-sm mt-2">
               {totalCount === 0 
                 ? "Great! Your posits seem to be answering questions well"
                 : "All gaps have been dismissed"
               }
             </p>
           </div>
         ) : (
           <>
             <div className="space-y-4">
               {visibleGaps.map((gap) => (
                 <div
                   key={gap.id}
                   className="border border-border rounded-lg p-3 md:p-3 hover:bg-muted/30 transition-colors relative"
                 >
                 {/* Dismiss button */}
                 <button
                   onClick={() => handleDismissGap(gap.id)}
                   className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                   title="Dismiss this knowledge gap"
                 >
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>

                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 pr-8">
                   <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                       <Badge variant="outline" className="text-xs">
                         {gap.posit.title}
                       </Badge>
                       <span className={`px-2 py-1 rounded-full text-xs ${getGapTypeColor(gap.gap_type)}`}>
                         {gap.gap_type.replace('-', ' ')}
                       </span>
                       <span className="text-xs text-muted-foreground">
                         {formatTimeAgo(gap.created_at)}
                       </span>
                     </div>
                    
                      <div className="space-y-2">
                        <div>
                          <div className="text-sm font-medium mb-1">Question</div>
                          <div className="text-sm bg-muted/60 p-2 rounded border border-border min-h-[36px]">
                            {gap.question || 'Question content not available'}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {gap.response ? (
                            gap.response.length > 140 ? `${gap.response.substring(0, 140)}…` : gap.response
                          ) : 'AI response not available'}
                        </div>

                        <div className="mt-2">
                          <div className="text-sm font-medium mb-1">Add your answer</div>
                          <Textarea
                            value={answers[gap.id] || ''}
                            onChange={(e) => handleAnswerChange(gap.id, e.target.value)}
                            placeholder="Write a concise answer to teach your Posit…"
                            className="text-sm"
                            rows={3}
                          />
                          <div className="mt-2 flex items-center gap-2">
                            <Button size="sm" onClick={() => handleSubmitAnswer(gap)} disabled={savingId === gap.id}>
                              {savingId === gap.id ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving</> : 'Save'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDismissGap(gap.id)}>
                              Dismiss
                            </Button>
                          </div>
                        </div>
                    </div>
                  </div>
                  </div>
                </div>
               ))}
             </div>

             {/* Pagination Controls */}
             {totalPages > 1 && (
               <div className="mt-6 flex items-center justify-between">
                 <div className="text-sm text-muted-foreground">
                   Showing {((currentPage - 1) * gapsPerPage) + 1} to {Math.min(currentPage * gapsPerPage, totalCount)} of {totalCount} knowledge gaps
                 </div>
                 <div className="flex items-center gap-2">
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={() => setCurrentPage(currentPage - 1)}
                     disabled={currentPage === 1}
                   >
                     <ChevronLeft className="h-4 w-4" />
                     Previous
                   </Button>
                   <span className="text-sm text-muted-foreground">
                     Page {currentPage} of {totalPages}
                   </span>
                   <Button
                     variant="outline"
                     size="sm"
                     onClick={() => setCurrentPage(currentPage + 1)}
                     disabled={currentPage === totalPages}
                   >
                     Next
                     <ChevronRight className="h-4 w-4" />
                   </Button>
                 </div>
               </div>
             )}

             <div className="mt-6 pt-4 border-t border-border text-center">
               <p className="text-xs text-muted-foreground">
                 Found {totalCount} knowledge gaps 
                 {dismissedGaps.size > 0 && ` (${dismissedGaps.size} dismissed)`} • 
                 These are opportunities to expand your posit content
               </p>
             </div>
           </>
         )}
       </CardContent>
     </Card>
   );
};

const RevenueInsightsTab = () => {
  const { user } = useAuth();

  const { data: revenueData, isLoading } = useQuery({
    queryKey: ['revenue-insights', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Get monetized posits
      const { data: monetizedPosits } = await supabase
        .from('posits')
        .select('id, title, subscription_price, created_at')
        .eq('user_id', user.id)
        .eq('is_monetized', true)
        .order('created_at', { ascending: false });

      if (!monetizedPosits || monetizedPosits.length === 0) {
        return {
          monetizedPosits: [],
          totalPotentialRevenue: 0,
          actualRevenue: 0,
          subscriberCount: 0
        };
      }

      // Calculate potential revenue (sum of each posit's price as a simple indicator)
      const totalPotentialRevenue = monetizedPosits.reduce((sum, posit) => {
        return sum + (posit.subscription_price || 0);
      }, 0);

      // Compute actual subscribers and revenue from posit_subscriptions
      const positIds = monetizedPosits.map(p => p.id);
      const { data: activeSubs } = await supabase
        .from('posit_subscriptions')
        .select('posit_id')
        .in('posit_id', positIds)
        .eq('status', 'active');

      const subscribersByPosit: Record<string, number> = {};
      (activeSubs || []).forEach((row: any) => {
        subscribersByPosit[row.posit_id] = (subscribersByPosit[row.posit_id] || 0) + 1;
      });

      // Pull last 30 days unique sessions per posit for conversion rate
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const { data: analyticsRows } = await supabase
        .from('posit_analytics')
        .select('posit_id, unique_sessions, date')
        .in('posit_id', positIds)
        .gte('date', thirtyDaysAgo.toISOString().split('T')[0]);
      const sessionsByPosit: Record<string, number> = {};
      (analyticsRows || []).forEach((r: any) => {
        sessionsByPosit[r.posit_id] = (sessionsByPosit[r.posit_id] || 0) + (r.unique_sessions || 0);
      });

      const revenueByPosit = monetizedPosits.map(posit => {
        const subscribers = subscribersByPosit[posit.id] || 0;
        const monthlyRevenue = subscribers * (posit.subscription_price || 0);
        const sessions = sessionsByPosit[posit.id] || 0;
        const conversionRate = sessions > 0 ? (subscribers / sessions) * 100 : 0;
        return {
          ...posit,
          subscribers,
          monthlyRevenue,
          conversionRate,
        };
      });

      const actualRevenue = revenueByPosit.reduce((sum, p) => sum + p.monthlyRevenue, 0);
      const subscriberCount = Object.values(subscribersByPosit).reduce((a, b) => a + b, 0);

      return {
        monetizedPosits,
        totalPotentialRevenue,
        actualRevenue,
        subscriberCount,
        revenueByPosit,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Revenue Insights</CardTitle>
          <CardDescription>Detailed earnings and subscription analytics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading revenue data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!revenueData || revenueData.monetizedPosits.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Revenue Insights</CardTitle>
          <CardDescription>Detailed earnings and subscription analytics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No monetized posits yet</p>
            <p className="text-sm mt-2">Enable monetization on your Posits to start earning revenue</p>
            <Button className="mt-4" size="sm" asChild>
              <Link to="/dashboard/posits">Monetize Posits</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue Insights</CardTitle>
        <CardDescription>Detailed earnings and subscription analytics</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Revenue Overview - Subtle */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
            <DollarSign className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold text-green-800">
              ${revenueData.actualRevenue.toFixed(0)}
            </p>
            <p className="text-sm text-green-600">Current Monthly Revenue</p>
          </div>
          
          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold text-blue-800">
              ${revenueData.totalPotentialRevenue.toFixed(0)}
            </p>
            <p className="text-sm text-blue-600">Potential Monthly Revenue</p>
          </div>
          
          <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
            <Users className="h-8 w-8 mx-auto mb-2 text-purple-600" />
            <p className="text-2xl font-bold text-purple-800">
              {revenueData.subscriberCount}
            </p>
            <p className="text-sm text-purple-600">Total Subscribers</p>
          </div>
        </div>

        {/* Revenue by Posit */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold mb-4">Revenue by Posit</h3>
          
          {revenueData.revenueByPosit?.map((posit) => (
            <div
              key={posit.id}
              className="flex items-center justify-between p-4 border border-border rounded-lg"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium">{posit.title}</h4>
                  <Badge variant="outline" className="text-xs">
                    ${posit.subscription_price}/month
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-muted-foreground">
                  <div>
                    <p>Subscribers</p>
                    <p className="font-medium text-foreground">{posit.subscribers}</p>
                  </div>
                  <div>
                    <p>Monthly Revenue</p>
                    <p className="font-medium text-green-600">${posit.monthlyRevenue.toFixed(0)}</p>
                  </div>
                  <div>
                    <p>Conversion Rate</p>
                    <p className="font-medium text-foreground">{posit.conversionRate.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p>Potential</p>
                    <p className="font-medium text-blue-600">${posit.subscription_price}/mo</p>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 ml-4">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/update-posit/${posit.id}`}>
                    Edit Pricing
                  </Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/chat/${posit.id}`}>
                    Preview
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Payment & Payout Info - Subtle */}
        <div className="mt-8 pt-6 border-t border-border">
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="text-sm font-medium mb-2">Payment Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div>
                <p>Next Payout: <span className="font-medium">1st of next month</span></p>
                <p>Processing Fee: <span className="font-medium">2.9% + $0.30</span></p>
              </div>
              <div>
                <p>Available Balance: <span className="font-medium text-green-600">$0.00</span></p>
                <p>Pending: <span className="font-medium text-yellow-600">$0.00</span></p>
              </div>
            </div>
            
            <div className="mt-3 pt-3 border-t border-border">
              <Button variant="outline" size="sm">
                View Detailed Reports
              </Button>
              <Button variant="outline" size="sm" className="ml-2">
                Payment Settings
              </Button>
            </div>
          </div>
        </div>
        
        <div className="mt-4 text-center">
          <p className="text-xs text-muted-foreground">
            Revenue data updates daily • All amounts in USD
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalyticsDashboard; 

// Conversations Tab – shows summarized conversations with quick actions
const ConversationsTab = () => {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const { data, isLoading } = useQuery({
    queryKey: ['conversations-summaries', user?.id, currentPage],
    queryFn: async () => {
      if (!user?.id) return { rows: [], total: 0 };
      // Fetch latest chat sessions for this creator's posits
      const { data: posits } = await supabase
        .from('posits')
        .select('id, title')
        .eq('user_id', user.id);
      const positIds = posits?.map(p => p.id) || [];
      if (positIds.length === 0) return { rows: [], total: 0 };

      // Total count for pagination
      const { count: total } = await supabase
        .from('chat_history')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds);

      // Page of sessions
      const { data: sessions } = await supabase
        .from('chat_history')
        .select('id, created_at, last_message_at, summary, posit_id, posits!inner(id, title)')
        .in('posit_id', positIds)
        .order('last_message_at', { ascending: false })
        .range((currentPage - 1) * perPage, currentPage * perPage - 1);

      return { rows: sessions || [], total: total || 0 };
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Conversations</CardTitle>
        <CardDescription>AI summaries with quick links to view the full thread</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading conversations…</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No conversations found</p>
            <p className="text-sm mt-2">Share your Posit to start receiving questions</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {rows.map((s: any) => (
                <div key={s.id} className="p-3 md:p-4 border border-border rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="outline" className="text-xs shrink-0">{s.posits?.title || s.posit_id}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(s.last_message_at || s.created_at).toLocaleString()}
                      </span>
                    </div>
                    <Button size="sm" variant="outline" className="w-full sm:w-auto" asChild>
                      <Link to={`/chat-details/${s.id}`}>View conversation</Link>
                    </Button>
                  </div>
                  <div className="text-sm text-muted-foreground whitespace-pre-line line-clamp-4">
                    {s.summary || 'No summary yet'}
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, total)} of {total} conversations
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Training Queue Tab – lists generated training questions and allows invoking more
const TrainingQueueTab = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedPosit, setSelectedPosit] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const { data: userPosits } = useQuery({
    queryKey: ['training-posits', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('posits')
        .select('id, title')
        .eq('user_id', user.id)
        .order('title');
      return data || [];
    },
    enabled: !!user?.id,
  });

  const { data: questionsData, isLoading, refetch } = useQuery({
    queryKey: ['training-questions', user?.id, selectedPosit, currentPage],
    queryFn: async () => {
      if (!user?.id) return { rows: [], total: 0 };

      // Build base query: get training questions for creator's posits
      const { data: posits } = await supabase
        .from('posits')
        .select('id')
        .eq('user_id', user.id);
      const positIds = posits?.map(p => p.id) || [];
      if (positIds.length === 0) return { rows: [], total: 0 };

      // Count
      let countQuery = supabase
        .from('training_questions')
        .select('*', { count: 'exact', head: true })
        .in('posit_id', positIds);
      if (selectedPosit !== 'all') countQuery = countQuery.eq('posit_id', selectedPosit);
      const { count: total } = await countQuery;

      // Page
      let pageQuery = supabase
        .from('training_questions')
        .select('id, posit_id, question, status, created_at, suggested_answer, reviewed_answer, embedding_status, posits!inner(id, title)')
        .in('posit_id', positIds)
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * perPage, currentPage * perPage - 1);
      if (selectedPosit !== 'all') pageQuery = pageQuery.eq('posit_id', selectedPosit);
      const { data, error } = await pageQuery;
      if (error) throw error;

      return { rows: data || [], total: total || 0 };
    },
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const totalPages = Math.ceil((questionsData?.total || 0) / perPage);

  const handleRunSelfTest = async () => {
    try {
      const targetPosit = selectedPosit !== 'all' ? selectedPosit : userPosits?.[0]?.id;
      if (!targetPosit) {
        toast({ title: 'No posit selected', description: 'Create or select a Posit first', variant: 'destructive' });
        return;
      }
      const { data, error } = await supabase.functions.invoke('posit-auto-train-invoke', {
        body: { posit_id: targetPosit, questions_requested: 5 }
      });
      if (error) throw error;
      toast({ title: 'Self-Test started', description: `${data?.created_questions?.length || 0} questions generated` });
      refetch();
    } catch (e: any) {
      toast({ title: 'Self-Test failed', description: e?.message || 'Please try again', variant: 'destructive' });
    }
  };

  useEffect(() => { setCurrentPage(1); }, [selectedPosit]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-lg md:text-xl">Training Queue</CardTitle>
            <CardDescription className="text-sm">Generated self-test questions waiting for review/embedding</CardDescription>
          </div>
          <div className="flex gap-2">
            <select
              value={selectedPosit}
              onChange={(e) => setSelectedPosit(e.target.value)}
              className="px-2 py-1 text-xs md:text-sm border border-border rounded-md bg-background"
            >
              <option value="all">All Posits</option>
              {userPosits?.map((p: any) => (
                <option key={p.id} value={p.id}>{p.title?.length > 24 ? `${p.title.substring(0,24)}...` : p.title}</option>
              ))}
            </select>
            <Button size="sm" variant="outline" onClick={handleRunSelfTest}>
              <TestTube className="w-3 h-3 mr-1" /> Run Self-Test
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const targetPosit = selectedPosit !== 'all' ? selectedPosit : userPosits?.[0]?.id;
                  if (!targetPosit) {
                    toast({ title: 'No posit selected', description: 'Create or select a Posit first', variant: 'destructive' });
                    return;
                  }
                  const { data, error } = await supabase.functions.invoke('auto-answer-training-questions', {
                    body: { posit_id: targetPosit, limit: 5 }
                  });
                  if (error) throw error;
                  toast({ title: 'Auto-Answer complete', description: `${data?.answered || 0} questions answered` });
                  refetch();
                } catch (e: any) {
                  toast({ title: 'Auto-Answer failed', description: e?.message || 'Please try again', variant: 'destructive' });
                }
              }}
            >
              Auto-Answer
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading training queue...</span>
          </div>
        ) : (questionsData?.rows?.length || 0) === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <TestTube className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No training questions yet</p>
            <p className="text-sm mt-2">Use Run Self-Test to generate starter questions for a Posit</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {questionsData?.rows?.map((q: any) => (
                <div key={q.id} className="p-3 md:p-4 border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-xs">{q.posits?.title || q.posit_id}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm">{q.question}</div>
                  <div className="mt-2 text-xs text-muted-foreground">Status: {q.status}</div>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={async () => {
                      try {
                        const { error } = await supabase.functions.invoke('embed-training-answer', {
                          body: { training_question_id: q.id, posit_id: q.posit_id }
                        });
                        if (error) throw error;
                        toast({ title: 'Embedded', description: 'Answer embedded to knowledge base' });
                        refetch();
                      } catch (e: any) {
                        toast({ title: 'Embed failed', description: e?.message || 'Please try again', variant: 'destructive' });
                      }
                    }}>Embed</Button>
                  </div>
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <ChevronLeft className="h-4 w-4" /> Previous
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};